// Domain types shared between the React frontend and Azure Functions API.
// These mirror the storage entity shape but use camelCase and Date for dates.
// Storage-layer entity types (with PartitionKey/RowKey/string-encoded dates)
// live in api/src/storage/entities.ts.
// --------------------------- Status ---------------------------
export const CASE_STATUSES = ['open', 'query_sent', 'reminder_sent', 'closed'];
export const CLOSED_REASONS = ['resolved', 'no_reply', 'not_applicable'];
export const TRANSITION_RULES = {
    open: ['query_sent', 'closed'],
    query_sent: ['reminder_sent', 'closed'],
    reminder_sent: ['closed'],
    closed: ['open', 'query_sent', 'reminder_sent'],
};
// --------------------------- Card type ---------------------------
// Card type derived from the (normalised) korttinumero prefix. Drives both the
// detection path at import time and the UI badge/filter. Label-only difference
// between Taksikortti and TaksikorttiPLUS — both use duplicate detection;
// Kertakortti uses the single-use violation flow.
export const CARD_TYPES = ['kertakortti', 'taksikortti', 'taksikortti_plus', 'tuntematon'];
/**
 * Map a korttinumero to its card type by leading-7-digit prefix.
 * Expects a NORMALISED korttinumero (trailing `:NNNN` already stripped).
 *   3084440… → kertakortti, 3084441… → taksikortti, 3084442… → taksikortti_plus.
 */
export function cardType(korttinumero) {
    if (korttinumero.startsWith('3084440'))
        return 'kertakortti';
    if (korttinumero.startsWith('3084441'))
        return 'taksikortti';
    if (korttinumero.startsWith('3084442'))
        return 'taksikortti_plus';
    return 'tuntematon';
}
// --------------------------- Trip ---------------------------
// Per-trip workflow status. Same lifecycle as a case — investigators contact
// each driver / company separately, so each trip on a card carries its own
// state. Case-level status (CardCase.status) is derived from the aggregate
// of trip statuses, not set directly by the operator.
export const TRIP_STATUSES = ['open', 'query_sent', 'reminder_sent', 'closed'];
export const TRIP_TRANSITION_RULES = {
    open: ['query_sent', 'closed'],
    query_sent: ['reminder_sent', 'closed'],
    reminder_sent: ['closed'],
    closed: ['open'],
};
// --------------------------- DuplicateGroup ---------------------------
/**
 * Customer-level duplicate-charge detection (separate from the per-card
 * violation flow). A group is the same customer + terminal (palvelutunnus) +
 * card charged ≥2 times within a short window (default 15 min). Routed by
 * card type at import time (Taksikortti / TaksikorttiPLUS — see {@link cardType}
 * + isDupPathCard in api/src/domain/duplicateGroups.ts). Operators review groups
 * and mark them review-required with a comment. Standalone — does NOT touch CardCase.
 */
// Unified with the kertakortti case ladder: pending → query_sent → reminder_sent
// → dismissed. 'review_required' is LEGACY (kept renderable/filterable; the UI no
// longer creates new ones). Raw values intentionally match CaseStatus strings so
// worklistState/StateBadge map both lifecycles with one table.
export const DUP_REVIEW_STATUSES = ['pending', 'query_sent', 'reminder_sent', 'review_required', 'dismissed'];
// ------------------- Unified worklist state (display layer) ----------------
// One shared vocabulary over both lifecycles (kertakortti CaseStatus + duplicate
// DupReviewStatus + per-trip TripStatus) so the UI shows consistent words +
// colours. DERIVE-only: the stored CaseStatus / DupReviewStatus values are NOT
// changed (CaseStatus is a Table PartitionKey — renaming it is a data migration).
export const WORKLIST_STATES = ['avoin', 'kysely', 'muistutus', 'tarkistettava', 'valmis'];
/** Lifecycle order for sorting (lower = earlier / more actionable). */
export const WORKLIST_STATE_RANK = {
    avoin: 0, kysely: 1, muistutus: 2, tarkistettava: 3, valmis: 4,
};
/** Map a raw CaseStatus / DupReviewStatus / TripStatus to the shared state. */
export function worklistState(raw) {
    switch ((raw ?? '').trim()) {
        case 'query_sent': return 'kysely';
        case 'reminder_sent': return 'muistutus';
        case 'review_required': return 'tarkistettava';
        case 'closed':
        case 'dismissed': return 'valmis';
        default: return 'avoin'; // 'open', 'pending', unknown → new/needs-triage
    }
}
// --------------------------- Charge state (invoice / payment) -------------
/**
 * Per-row invoice (Myyntilaskutustila) / settlement (Tilitystila) state, for
 * worklist filtering + "handle imminent first" prioritisation. Derived from the
 * raw status string — a DISPLAY / QUERY concept, independent of the
 * isInvoiced/isSettled done-classification predicates.
 *
 *   approved = "Hyväksytty laskutukseen" (will be billed/paid SOON → handle first)
 *   active   = "Laskutettu" (already billed / settled)
 *   blocked  = "Ei laskuteta" / "Ei voida hyväksyä laskutukseen"
 *   pending  = blank / "Selvittämättä" / unknown (not yet processed)
 *   none     = row has no charges
 */
export const CHARGE_STATES = ['approved', 'active', 'pending', 'blocked', 'none'];
/** "Handle first" ranking — lower = more urgent. */
export const CHARGE_STATE_RANK = {
    approved: 0, pending: 1, active: 2, blocked: 3, none: 4,
};
const BLOCKED_INVOICE_STATUSES = new Set(['Ei laskuteta', 'Ei voida hyväksyä laskutukseen']);
/** Map a raw Myyntilaskutustila / Tilitystila value to a ChargeState. */
export function chargeState(raw) {
    const s = (raw ?? '').trim();
    if (s === 'Hyväksytty laskutukseen')
        return 'approved';
    if (s === 'Laskutettu')
        return 'active';
    if (BLOCKED_INVOICE_STATUSES.has(s))
        return 'blocked';
    return 'pending';
}
/**
 * Row-level state = the most urgent (min-rank) among a row's charges; `[]` → `none`.
 * Because `approved` is rank 0, a row aggregates to `approved` iff any charge is
 * approved — so an `invoiceState=approved` filter is exactly "row has an imminent bill".
 */
export function aggregateChargeState(states) {
    let best = 'none';
    for (const s of states)
        if (CHARGE_STATE_RANK[s] < CHARGE_STATE_RANK[best])
            best = s;
    return best;
}
// --------------------------- CaseEvent ---------------------------
export const EVENT_KINDS = [
    'status_changed', 'flagged', 'unflagged', 'note_added',
    'reassigned', 'reopened', 'migrated',
    'trip_status_changed', 'trip_closed', 'trip_reopened',
    'auto_closed',
    // Duplicate-group workflow audit
    'dup_marked_review', 'dup_dismissed', 'dup_reopened', 'dup_reassigned',
    'dup_query_sent', 'dup_reminder_sent',
];
// --------------------------- ImportLog ---------------------------
// --------------------------- Notification ---------------------------
export const NOTIFICATION_KINDS = [
    'case_assigned',
    'case_status_changed',
    'case_note_added',
    'case_flagged',
];
//# sourceMappingURL=types.js.map